import {
  computeCustomIngredientLineCost,
  computeIngredientLineCost,
  extractAndCalculateSize,
  findVenueProductForIngredient,
  resolveIngredientUnitPrice,
} from 'src/utils/priceFunctions'

/** Ingredient line sum vs persisted header — same tolerance as RecipeDetails. */
export const RECIPE_ECONOMICS_STALE_TOLERANCE = 0.05

export const recipeEconomicsAreStale = (
  persistedCostPerRecipe,
  computedLineSum,
  tolerance = RECIPE_ECONOMICS_STALE_TOLERANCE,
) => {
  const persisted = Number(persistedCostPerRecipe)
  const computed = Number(computedLineSum)

  if (!Number.isFinite(persisted) || !Number.isFinite(computed)) {
    return false
  }

  return Math.abs(persisted - computed) >= tolerance
}

const computeCatalogIngredientLineTotal = (
  ingredient,
  venueProductsCatalog,
) => {
  const venueProduct = findVenueProductForIngredient(
    ingredient,
    venueProductsCatalog,
  )
  const selectedSize =
    ingredient?.size ||
    extractAndCalculateSize(ingredient?.product?.size || '').numbers?.[0] ||
    0
  const price = resolveIngredientUnitPrice({
    venueProduct,
    product: ingredient?.product,
  })

  return (
    computeIngredientLineCost({
      productSize: ingredient?.product?.size || '',
      unitPrice: price,
      selectedSize,
      UoM: ingredient?.UoM,
      conversion: ingredient?.conversion,
    }).lineTotal || 0
  )
}

export const computeRecipeIngredientLineSum = (
  recipe,
  { recipeById = new Map(), venueProductsCatalog = [] } = {},
) => {
  let lineSum = 0

  for (const ingredient of recipe?.ingredients || []) {
    lineSum += computeCatalogIngredientLineTotal(
      ingredient,
      venueProductsCatalog,
    )
  }

  for (const customIngredient of recipe?.customIngredients || []) {
    lineSum += computeCustomIngredientLineCost(customIngredient).lineTotal || 0
  }

  for (const recipeIngredient of recipe?.recipeIngredients || []) {
    const baseRecipeId = recipeIngredient?.recipeId || recipeIngredient?.recipe
    const base = recipeById.get(baseRecipeId)
    const isLinked = recipeIngredient?.isLinked !== false
    const costPerPortion = Number(
      isLinked && base?.costPerPortion != null
        ? base.costPerPortion
        : recipeIngredient?.costPerPortion ?? base?.costPerPortion,
    )
    const portions = Number(recipeIngredient?.portions) || 0

    if (costPerPortion > 0 && portions > 0) {
      lineSum += costPerPortion * portions
    }
  }

  return Math.round(lineSum * 100) / 100
}

export const getRecipeEconomicsStaleState = (
  recipe,
  { recipeById = new Map(), venueProductsCatalog = [] } = {},
) => {
  const lineSum = computeRecipeIngredientLineSum(recipe, {
    recipeById,
    venueProductsCatalog,
  })
  const persistedCostPerRecipe = Number(recipe?.costPerRecipe)
  const isStale = recipeEconomicsAreStale(persistedCostPerRecipe, lineSum)

  return {
    lineSum,
    persistedCostPerRecipe,
    isStale,
  }
}

export const countRecipesWithStaleEconomics = (recipes = [], context = {}) =>
  recipes.filter(
    recipe => getRecipeEconomicsStaleState(recipe, context).isStale,
  ).length
